import React, { useState, useCallback, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, ChevronDown, RefreshCw, Copy, Gift, Upload } from 'lucide-react';
import { useCustomersPaginated } from '../../hooks/useCustomersPaginated';
import { formatters } from '../../utils/formatters';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import CustomerForm from './CustomerForm';
import CsvImportModal from './CsvImportModal';
import { useAuth } from '../../contexts/AuthContext';

const CustomersManager = () => {
  const { user } = useAuth();
  const { 
    customers, 
    loading, 
    error,
    hasMore,
    totalCount,
    searchTerm,
    sortField,
    sortDirection,
    pageSize,
    updateSearch,
    updateSort,
    updatePageSize,
    loadNextPage,
    loadFirstPage,
    addCustomer, 
    updateCustomer, 
    deleteCustomer 
  } = useCustomersPaginated();
  
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [showCsvImport, setShowCsvImport] = useState(false);

  // Função para verificar se o cliente faz aniversário no mês atual
  const isBirthdayThisMonth = useCallback((birthDate) => {
    if (!birthDate) return false;
    
    try {
      let dateObj;
      
      // Se é uma string ISO (formato do Firebase)
      if (typeof birthDate === 'string') {
        dateObj = new Date(birthDate);
      }
      // Se é um Timestamp do Firebase
      else if (birthDate.toDate && typeof birthDate.toDate === 'function') {
        dateObj = birthDate.toDate();
      }
      // Se é um objeto Date
      else if (birthDate instanceof Date) {
        dateObj = birthDate;
      }
      else {
        return false;
      }
      
      // Verificar se a data é válida
      if (isNaN(dateObj.getTime())) {
        return false;
      }
      
      const currentMonth = new Date().getMonth();
      const birthMonth = dateObj.getMonth();
      
      return currentMonth === birthMonth;
    } catch (error) {
      console.error('Erro ao verificar aniversário:', error);
      return false;
    }
  }, []);

  // Função para copiar código de indicação
  const handleCopyReferralCode = useCallback(async (code, customerName) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeout = setTimeout(() => {
      updateSearch(localSearchTerm);
    }, 500);
    
    return () => clearTimeout(timeout);
  }, [localSearchTerm, updateSearch]);

  // Update local search term
  const handleSearchChange = useCallback((value) => {
    setLocalSearchTerm(value);
  }, []);

  // Sort options
  const sortOptions = [
    { value: 'name:asc', label: 'Nome (A-Z)' },
    { value: 'name:desc', label: 'Nome (Z-A)' },
    { value: 'createdAt:desc', label: 'Mais recentes' },
    { value: 'createdAt:asc', label: 'Mais antigos' },
    { value: 'points:desc', label: 'Mais pontos' },
    { value: 'points:asc', label: 'Menos pontos' }
  ];

  const currentSortValue = `${sortField}:${sortDirection}`;

  const handleSortChange = (value) => {
    const [field, direction] = value.split(':');
    updateSort(field, direction);
  };

  const handlePageSizeChange = (value) => {
    updatePageSize(parseInt(value));
  };

  // Page size options
  const pageSizeOptions = [
    { value: 10, label: '10 por página' },
    { value: 20, label: '20 por página' },
    { value: 50, label: '50 por página' },
    { value: 100, label: '100 por página' }
  ];

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setShowForm(true);
  };

  const handleDelete = async (customerId) => {
    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      try {
        await deleteCustomer(customerId);
      } catch (error) {
        alert('Erro ao excluir cliente: ' + error.message);
      }
    }
  };

  const handleSubmit = async (customerData) => {
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, customerData);
      } else {
        await addCustomer(customerData);
      }
      setShowForm(false);
      setEditingCustomer(null);
    } catch (error) {
      alert('Erro ao salvar cliente: ' + error.message);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCustomer(null);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadNextPage();
    }
  };

  const handleRefresh = () => {
    loadFirstPage();
  };

  const handleImportComplete = (results) => {
    if (results.processed > 0) {
      // Recarregar a lista de clientes após importação
      loadFirstPage();
      
      // Mostrar mensagem de sucesso
      if (results.created > 0 && results.updated > 0) {
        alert(`Importação concluída! ${results.created} clientes criados e ${results.updated} atualizados.`);
      } else if (results.created > 0) {
        alert(`Importação concluída! ${results.created} clientes criados.`);
      } else if (results.updated > 0) {
        alert(`Importação concluída! ${results.updated} clientes atualizados.`);
      }
    }
    
    setShowCsvImport(false);
  };

  if (loading && customers.length === 0) {
    return <LoadingSpinner size="lg" className="py-20" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Gerenciar Clientes</h2>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCsvImport(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Upload className="w-5 h-5" />
            <span>Importar CSV</span>
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Novo Cliente</span>
          </button>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Campo de busca */}
          <div className="flex-1">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, CPF/CNPJ, telefone ou email..."
                value={localSearchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {localSearchTerm !== searchTerm && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                </div>
              )}
            </div>
          </div>

          {/* Seletor de ordenação */}
          <div className="md:w-64">
            <div className="relative">
              <select
                value={currentSortValue}
                onChange={(e) => handleSortChange(e.target.value)}
                className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Seletor de quantidade por página */}
          <div className="md:w-48">
            <div className="relative">
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {pageSizeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Botão de refresh */}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Contadores */}
        <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
          <span>
            Mostrando {customers.length} de {searchTerm ? totalCount : '...'} clientes
          </span>
          <div className="flex items-center space-x-4">
            {searchTerm && (
              <span>
                Filtrado por: "{searchTerm}"
              </span>
            )}
            <span className="text-xs text-gray-500">
              {pageSize} por página
            </span>
          </div>
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Erro: {error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 text-red-700 hover:text-red-800 font-medium"
          >
            Tentar novamente
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {/* Legenda para aniversariantes */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center text-sm text-gray-600">
            <Gift className="w-4 h-4 text-purple-500 mr-2" />
            <span>Clientes com linha destacada fazem aniversário neste mês</span>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CPF/CNPJ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Nascimento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pontos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código Indicação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {customers.map(customer => {
                const hasBirthdayThisMonth = isBirthdayThisMonth(customer.birthDate);
                const rowClasses = hasBirthdayThisMonth 
                  ? "bg-purple-50 border-l-4 border-purple-400 hover:bg-purple-100" 
                  : "hover:bg-gray-50";
                
                return (
                <tr key={customer.id} className={rowClasses}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="font-medium text-gray-900">{customer.name}</div>
                      {hasBirthdayThisMonth && (
                        <Gift className="w-4 h-4 text-purple-500 ml-2" title="Aniversário este mês!" />
                      )}
                    </div>
                    {(customer.emails && customer.emails.length > 0) ? (
                      <div className="text-sm text-gray-500">
                        {customer.emails.length === 1 ? (
                          customer.emails[0]
                        ) : (
                          <div>
                            <div>{customer.emails[0]}</div>
                            <div className="text-xs text-gray-400">
                              +{customer.emails.length - 1} outro{customer.emails.length > 2 ? 's' : ''}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : customer.email ? (
                      <div className="text-sm text-gray-500">{customer.email}</div>
                    ) : null}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {formatters.formatCpfOrCnpj(customer.cpf)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {customer.phone ? formatters.formatPhone(customer.phone) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    <div className="flex items-center">
                      {hasBirthdayThisMonth ? (
                        <div className="flex items-center bg-purple-100 border border-purple-300 rounded-lg px-2 py-1">
                          <Gift className="w-4 h-4 text-purple-600 mr-1" />
                          <span className="text-purple-800 font-medium text-sm">
                            {formatters.formatDate(customer.birthDate)}
                          </span>
                        </div>
                      ) : (
                        <span>{formatters.formatDate(customer.birthDate)}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-sm font-medium">
                      {customer.points} pts
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {customer.referralCode ? (
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center bg-green-50 border border-green-200 rounded-lg px-3 py-1">
                          <Gift className="w-4 h-4 text-green-600 mr-2" />
                          <span className="text-sm font-mono text-green-800">{customer.referralCode}</span>
                        </div>
                        <button
                          onClick={() => handleCopyReferralCode(customer.referralCode, customer.name)}
                          className="text-gray-500 hover:text-gray-700 transition-colors"
                          title="Copiar código"
                        >
                          {copiedCode === customer.referralCode ? (
                            <span className="text-green-600 text-xs font-medium">Copiado!</span>
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(customer)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title="Editar cliente"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(customer.id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Excluir cliente"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          
          {customers.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">
              {searchTerm ? (
                <div>
                  <p>Nenhum cliente encontrado para "{searchTerm}"</p>
                  <button
                    onClick={() => {
                      setLocalSearchTerm('');
                      updateSearch('');
                    }}
                    className="mt-2 text-blue-600 hover:text-blue-800"
                  >
                    Limpar filtro
                  </button>
                </div>
              ) : (
                <p>Nenhum cliente cadastrado ainda</p>
              )}
            </div>
          )}
        </div>

        {/* Controles de paginação */}
        {customers.length > 0 && (
          <div className="bg-gray-50 px-6 py-3 border-t">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {loading && customers.length > 0 && (
                  <span className="flex items-center">
                    <LoadingSpinner size="sm" className="mr-2" />
                    Carregando mais...
                  </span>
                )}
              </div>
              
              <div className="flex items-center space-x-3">
                {hasMore && (
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Carregando...' : 'Carregar mais'}
                  </button>
                )}
                
                {!hasMore && customers.length > 0 && (
                  <span className="text-sm text-gray-500">
                    Todos os clientes foram carregados
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={handleCloseForm}
        title={editingCustomer ? 'Editar Cliente' : 'Novo Cliente'}
      >
        <CustomerForm
          customer={editingCustomer}
          onSubmit={handleSubmit}
          onCancel={handleCloseForm}
        />
      </Modal>

      <CsvImportModal
        isOpen={showCsvImport}
        onClose={() => setShowCsvImport(false)}
        companyId={user?.uid}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default CustomersManager;